import { useEffect } from 'react';
import { useDiagramStore } from '../../../stores/diagramStore';
import { useDiagramInfo } from '../../../hooks/useDiagramInfo';
import { useOrgModel } from './useOrgModel';
import { computeOrgChartRecordLinks } from '../services/OrgChartRecordLinkService';

const LINK_DEBOUNCE_MS = 500;

/**
 * Binds fresh org-chart canvas nodes to their workspace folder records.
 *
 * Charts created before the folder gained a repository source (or via the
 * canvas CSV import) carry no `attrs.folderRecordId`, which keeps
 * record-gated surfaces — succession drafting above all — permanently
 * disabled. This hook matches unlinked person/position nodes against the
 * permissioned org-model projection (externalId / bound record id / unique
 * e-mail) and stamps the record UUID onto the shape through the normal
 * store→Yjs write path.
 *
 * Protected-Yjs boundary: only the record UUID pointer is written — the same
 * contract as `bindMigratedShapeRecord` and the position-chart scene builder.
 * Matching is idempotent and never overwrites an existing link, so repeat
 * passes converge to a no-op.
 */
export function useOrgChartRecordLinking(diagramId: string | undefined): void {
  const { data: diagram } = useDiagramInfo(diagramId);
  const isPlainOrgChart = diagram?.type === 'org' && !diagram?.viewDefinition;
  const enabled = Boolean(isPlainOrgChart && diagram?.folderId);
  const orgModel = useOrgModel({ folderId: diagram?.folderId, enabled });
  const model = enabled ? orgModel.data : undefined;

  useEffect(() => {
    if (!model) return;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const stampLinks = () => {
      timer = null;
      const state = useDiagramStore.getState();
      if (!state.canWrite || state.isInitialLoading) return;
      const links = computeOrgChartRecordLinks(state.nodes, state.edges, model);
      if (links.length === 0) return;
      const nodesById = new Map(state.nodes.map((node) => [node.id, node]));
      state.updateNodes(links.flatMap(({ nodeId, folderRecordId }) => {
        const node = nodesById.get(nodeId);
        if (!node) return [];
        return [{ id: nodeId, updates: { attrs: { ...node.attrs, folderRecordId } } }];
      }));
    };

    const schedule = () => {
      if (timer != null) clearTimeout(timer);
      timer = setTimeout(stampLinks, LINK_DEBOUNCE_MS);
    };

    schedule();
    const unsubscribe = useDiagramStore.subscribe((state, previous) => {
      if (state.nodes !== previous.nodes
        || state.canWrite !== previous.canWrite
        || state.isInitialLoading !== previous.isInitialLoading) {
        schedule();
      }
    });
    return () => {
      unsubscribe();
      if (timer != null) clearTimeout(timer);
    };
  }, [model]);
}
